import { jsx as _jsx } from "react/jsx-runtime";
/**
 * PricingPermissionsContext
 * React context for sharing pricing permission state across components
 */
import { createContext, useContext, useMemo } from 'react';
import { usePricingPermissions } from '../hooks/usePricingPermissions';
const defaultContextValue = {
    canEditPricing: false,
    canEditQuantity: false,
    getQuantityTooltip: () => 'Permissions not loaded',
    isPricingFieldEditable: () => false,
    getPricingTooltip: () => 'Pricing permissions not loaded',
    currentStageName: '',
    closedDaysRemaining: undefined,
    closedEditExpired: false,
    isClosedWorkOrder: false,
    permissions: null,
    isLoaded: false,
};
const PricingPermissionsContext = createContext(defaultContextValue);
/**
 * Provider component for pricing permissions context
 *
 * @example
 * ```tsx
 * <PricingPermissionsProvider
 *   permissions={workOrder.pricing_permissions}
 *   isSystemAdmin={user.isSystemAdmin}
 * >
 *   <WorkOrderServiceCard service={service} />
 * </PricingPermissionsProvider>
 * ```
 */
export function PricingPermissionsProvider({ children, permissions, userPermissions = [], isSystemAdmin = false, }) {
    const hookResult = usePricingPermissions({
        permissions,
        userPermissions,
        isSystemAdmin,
    });
    const contextValue = useMemo(() => ({
        ...hookResult,
        isLoaded: true,
    }), [hookResult]);
    return _jsx(PricingPermissionsContext.Provider, { value: contextValue, children: children });
}
/**
 * Hook to access pricing permissions from context
 *
 * @example
 * ```tsx
 * function ServicePricingField() {
 *   const { canEditPricing, getPricingTooltip } = usePricingPermissionsContext();
 *
 *   return (
 *     <LockedFormInput
 *       locked={!canEditPricing}
 *       lockedTooltip={getPricingTooltip()}
 *       value={price}
 *     />
 *   );
 * }
 * ```
 */
export function usePricingPermissionsContext() {
    const context = useContext(PricingPermissionsContext);
    return context;
}
export default PricingPermissionsContext;
